import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

import { loadXomicsTranslations } from './api';

const STORAGE_KEY = 'xomics-language';
const RTL_LANGUAGES = new Set(['ar', 'fa', 'he', 'ur']);
const I18nContext = createContext(null);

let activeTranslations = Object.freeze({});

function normaliseLanguage(value) {
  return String(value || '').trim().split(/[-_]/, 1)[0].toLowerCase() || 'en';
}

function initialLanguage() {
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved) return normaliseLanguage(saved);
  return normaliseLanguage(window.navigator.language || 'en');
}

function interpolate(template, variables) {
  if (!variables) return template;
  return template.replace(/\{([A-Za-z][A-Za-z0-9_]*)\}/g, (match, name) => (
    Object.prototype.hasOwnProperty.call(variables, name)
      ? String(variables[name] ?? '')
      : match
  ));
}

// Runtime code carries stable keys only. English and every translated value
// come from Core_AdminPortal.xomics_translations; a missing database key stays
// conspicuous instead of silently becoming hardcoded English.
export function tx(key, variables) {
  const value = activeTranslations[key];
  return interpolate(typeof value === 'string' ? value : `⟦${key}⟧`, variables);
}

// For keys built from data (an app event's name), whether the catalogue has one.
export function hasTx(key) {
  return typeof activeTranslations[key] === 'string';
}

export function XomicsI18nProvider({ children }) {
  const [catalogue, setCatalogue] = useState({
    status: 'loading',
    language: initialLanguage(),
    revision: '',
    languages: [],
    fallbacks: [],
  });

  const changeLanguage = useCallback(async (requested) => {
    const language = normaliseLanguage(requested);
    if (catalogue.status === 'ready' && catalogue.language === language) return;
    setCatalogue((current) => ({ ...current, status: 'loading' }));
    try {
      const loaded = await loadXomicsTranslations(language);
      activeTranslations = Object.freeze({ ...loaded.translations });
      window.localStorage.setItem(STORAGE_KEY, loaded.language);
      setCatalogue({
        status: 'ready',
        language: loaded.language,
        revision: loaded.revision,
        languages: loaded.languages,
        fallbacks: loaded.fallbacks,
      });
    } catch (error) {
      if (language !== 'en') {
        const loaded = await loadXomicsTranslations('en');
        activeTranslations = Object.freeze({ ...loaded.translations });
        window.localStorage.setItem(STORAGE_KEY, 'en');
        setCatalogue({
          status: 'ready',
          language: 'en',
          revision: loaded.revision,
          languages: loaded.languages,
          fallbacks: loaded.fallbacks,
        });
        return;
      }
      setCatalogue((current) => ({ ...current, status: 'failed' }));
    }
  }, [catalogue.language, catalogue.status]);

  useEffect(() => {
    changeLanguage(catalogue.language).catch(() => {
      setCatalogue((current) => ({ ...current, status: 'failed' }));
    });
    // Initial catalogue load only. Further changes go through changeLanguage.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const previousLanguage = document.documentElement.lang;
    const previousDirection = document.documentElement.dir;
    document.documentElement.lang = catalogue.language;
    document.documentElement.dir = RTL_LANGUAGES.has(catalogue.language) ? 'rtl' : 'ltr';
    return () => {
      document.documentElement.lang = previousLanguage;
      document.documentElement.dir = previousDirection;
    };
  }, [catalogue.language]);

  const value = useMemo(() => ({
    ...catalogue,
    direction: RTL_LANGUAGES.has(catalogue.language) ? 'rtl' : 'ltr',
    setLanguage: changeLanguage,
    t: tx,
  }), [catalogue, changeLanguage]);

  if (catalogue.status !== 'ready') {
    return (
      <main id="xo" className="xo-auth" aria-busy="true">
        <div className="xo-loader" />
      </main>
    );
  }

  // Remounting on an explicit language change makes every plain tx() call,
  // including labels constructed by helpers outside JSX, evaluate against the
  // new catalogue at once. A preference change is rare and already refreshes
  // the authenticated account, so preserving half-completed form state would
  // be more surprising than restarting the current screen cleanly.
  return (
    <I18nContext.Provider value={value}>
      <React.Fragment key={`${catalogue.language}:${catalogue.revision}`}>
        {children}
      </React.Fragment>
    </I18nContext.Provider>
  );
}

export function useXomicsI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useXomicsI18n must be used inside XomicsI18nProvider');
  return context;
}
